import { Alert, Box, Button, Chip, DialogContent, Divider, MenuItem, MenuList, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { TemplateFixtureReplay, TemplateFixtureReplayCriterion } from '../../apiClients/TemplateFixtureReplayClient';
import { UpsertAction } from '../../Enums';
import useViewportMode from '../../hooks/useViewportMode';
import { WeightSelection, ProficiencyGraph, TemplateRevision2ProficiencyGroupDisplay } from '../../models/dtos';
import { ProfileGraph } from '../../apiClients/ProfileClient';
import ProficiencyGraphsGrid from '../../popups/ProficiencyGraphsGrid';
import PanelSegment from '../PanelSegment';
import { ResponsiveDialog, ResponsiveDialogHeader, StickyDialogActions } from '../responsive';
import ButtonEx from '../ButtonEx';
import {
    createSuccessProfileDecisionReplay,
    SUCCESS_PROFILE_DECISION_REPLAY_VERSION,
    SuccessProfileDecisionProposal,
} from '../../services/SuccessProfileDecisionReplayService';

const SYNTHETIC_PROFILE_ID = 'synthetic-template-replay-profile';
const SYNTHETIC_TEMPLATE_REVISION_ID = 'synthetic-template-replay-revision';

const groupId = (group: string) => `synthetic-group-${group.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;

const selectionValue = (criterion: TemplateFixtureReplayCriterion, replay: TemplateFixtureReplay) => {
    const legend = criterion.Type === 'Ability' ? replay.AbilityLegend : replay.BehaviorLegend;
    return legend.indexOf(criterion.SelectedRating);
};

export const toReadOnlyTemplateGraph = (replay: TemplateFixtureReplay): ProfileGraph => {
    const registeredGroups = replay.ProficiencyGroups ?? [];
    const populatedGroups = replay.Criteria.reduce<string[]>((result, criterion) =>
        result.includes(criterion.Group) ? result : [...result, criterion.Group], []);
    const groups = [...registeredGroups, ...populatedGroups.filter(group => !registeredGroups.includes(group))];
    const displays: TemplateRevision2ProficiencyGroupDisplay[] = groups.map((group, index) => ({
        Id: `synthetic-template-group-${index + 1}`,
        TemplateRevisionId: SYNTHETIC_TEMPLATE_REVISION_ID,
        ProficiencyGroupId: groupId(group),
        Weight: 0,
        TemplateName: replay.TemplateName,
        ProficiencyGroupName: group,
    }));
    const proficiencies: ProficiencyGraph[] = replay.Criteria.map((criterion, index) => ({
        ProficiencySelectionId: `synthetic-selection-${index + 1}`,
        ProficiencyId: `synthetic-criterion-${index + 1}`,
        TemplateId: null,
        AssociationId: SYNTHETIC_PROFILE_ID,
        ProficiencyGroupId: groupId(criterion.Group),
        ProficiencyGroupName: criterion.Group,
        Type: criterion.Type === 'Ability' ? 'Ability' : 'Behavior',
        Name: criterion.Name,
        Selection: {
            Type: criterion.Type === 'Ability' ? 'Ability' : 'Behavior',
            SelectionText: criterion.SelectedRating,
            Value: selectionValue(criterion, replay),
        },
        Weight: criterion.Weight as WeightSelection,
        IsNegative: criterion.IsCounterproductive,
        exclude: false,
        Notes: '',
    }));

    return {
        TemplateRevisionId: SYNTHETIC_TEMPLATE_REVISION_ID,
        Profile: {
            Id: SYNTHETIC_PROFILE_ID,
            Name: replay.TemplateName,
            IsActive: true,
            IsDeleted: false,
            IsSelected: true,
        },
        Proficiencies: proficiencies,
        TemplateRevision2ProficiencyGroupDisplays: displays,
        ScoreCard: null,
    };
};

const denyMutation = (): never => {
    throw new Error('Synthetic template replay is read-only.');
};

const Legend = ({ label, levels, helperText }: { label: string; levels: string[]; helperText?: string }) => <Stack
    component="section"
    aria-label={label}
    direction={{ xs: 'column', sm: 'row' }}
    spacing={1}
    alignItems={{ sm: 'center' }}
    sx={{ minWidth: 0 }}>
    <Typography variant="subtitle2" sx={{ flex: '0 0 auto' }}>{label}:</Typography>
    <Stack direction="row" flexWrap="wrap" sx={{ gap: 0.75 }}>
        {levels.map((level, index) => <Chip key={level} size="small" label={`${index + 1}. ${level}`} />)}
    </Stack>
    {helperText && <Typography variant="caption" color="text.secondary">{helperText}</Typography>}
</Stack>;

interface SyntheticTemplateReplayPresentationProps {
    replay: TemplateFixtureReplay;
    onClose?: () => void;
    onBack?: () => void;
    showDecisionReplay?: boolean;
}

const SyntheticTemplateReplayPresentation = ({ replay, onClose = () => undefined, onBack, showDecisionReplay = false }: SyntheticTemplateReplayPresentationProps) => {
    const viewport = useViewportMode();
    const compact = viewport.isPhone || (viewport.isTablet && viewport.isPortrait);
    const graph = useMemo(() => toReadOnlyTemplateGraph(replay), [replay]);
    const [selectedGroupId, setSelectedGroupId] = useState(graph.TemplateRevision2ProficiencyGroupDisplays[0]?.ProficiencyGroupId ?? '');
    const [decisionProposals, setDecisionProposals] = useState<SuccessProfileDecisionProposal[]>(createSuccessProfileDecisionReplay);
    const selectedGroup = graph.TemplateRevision2ProficiencyGroupDisplays.find(group => group.ProficiencyGroupId === selectedGroupId)
        ?? graph.TemplateRevision2ProficiencyGroupDisplays[0];

    const decide = (id: string, status: 'Included' | 'Denied') => setDecisionProposals(previous =>
        previous.map(proposal => proposal.Id === id ? { ...proposal, Status: status } : proposal));

    return <ResponsiveDialog
        open
        fullWidth
        maxWidth="xl"
        widthTier="wide"
        sizingStrategy="workspace"
        desktopViewportGutter={32}
        onClose={onClose}
        aria-labelledby="synthetic-template-replay-title"
        PaperProps={{
            sx: {
                height: 'calc(100% - 64px)',
                maxHeight: 'calc(100% - 64px)',
                overflow: 'hidden',
            },
        }}>
        <ResponsiveDialogHeader
            id="synthetic-template-replay-title"
            title={replay.TemplateName}
            actions={<Chip label="Synthetic demonstration · Read only" color="info" variant="outlined" />}
            onClose={onClose}
            viewportOverride={viewport.mode}
        />
        <DialogContent
            dividers
            data-testid="synthetic-template-replay-content"
            sx={{ minHeight: 0, overflowY: 'auto', overflowX: 'hidden', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Alert
                severity="info"
                variant="filled"
                role="status"
                data-testid="synthetic-template-replay-banner"
                sx={{ flex: '0 0 auto', '& .MuiAlert-message': { width: '100%' } }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                    <Typography fontWeight={800}>SYNTHETIC DEMONSTRATION · READ ONLY</Typography>
                    <Typography>{replay.OrganizationName} · {replay.ScenarioName}</Typography>
                </Stack>
            </Alert>
            <Stack spacing={1} sx={{ flex: '0 0 auto' }}>
                <Legend label="Ability rating scale" levels={replay.AbilityLegend} />
                <Legend
                    label="Behavior frequency scale"
                    levels={replay.BehaviorLegend}
                    helperText="Lower frequency is better for counterproductive behavior."
                />
            </Stack>
            {showDecisionReplay && <Paper
                variant="outlined"
                component="section"
                data-testid="success-profile-decision-replay"
                data-fixture-fingerprint={SUCCESS_PROFILE_DECISION_REPLAY_VERSION}
                sx={{ flex: '0 0 auto', p: 1.5 }}>
                <Stack spacing={1.25}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} alignItems={{ sm: 'center' }} justifyContent="space-between">
                        <Box>
                            <Typography variant="subtitle1" fontWeight={800}>Generated Success Profile criteria</Typography>
                            <Typography variant="caption" color="text.secondary">Included by default · deny only criteria that do not belong</Typography>
                        </Box>
                    </Stack>
                    <Divider />
                    <Box sx={{
                        display: 'flex',
                        gap: 1,
                        overflowX: 'auto',
                        pb: 0.5,
                    }}>
                        {decisionProposals.map(proposal => <Paper key={proposal.Id} variant="outlined" sx={{ p: 1.25, minWidth: 280, flex: '0 0 280px' }}>
                            <Stack spacing={0.75}>
                                <Stack direction="row" gap={0.5} flexWrap="wrap">
                                    <Chip size="small" label={proposal.Kind} variant="outlined" />
                                    <Chip size="small" label={proposal.Status} color={proposal.Status === 'Included' ? 'success' : 'default'} />
                                </Stack>
                                <Typography variant="caption" color="text.secondary">{proposal.Group}</Typography>
                                <Typography variant="body2" fontWeight={800} sx={{ overflowWrap: 'anywhere' }}>{proposal.Name}</Typography>
                                <Typography variant="body2">Proposed: <strong>{proposal.ProposedRating}</strong></Typography>
                                <Typography variant="caption" color="text.secondary">{proposal.Evidence}</Typography>
                                <Stack direction="row" gap={0.75} justifyContent="flex-end">
                                    {proposal.Status === 'Denied'
                                        ? <Button size="small" onClick={() => decide(proposal.Id, 'Included')}>Undo denial</Button>
                                        : <Button size="small" color="inherit" aria-label={`Deny ${proposal.Name}`} onClick={() => decide(proposal.Id, 'Denied')}>Deny</Button>}
                                </Stack>
                            </Stack>
                        </Paper>)}
                    </Box>
                </Stack>
            </Paper>}
            <Box
                data-testid="synthetic-template-replay-workspace"
                data-layout={compact ? 'compact' : viewport.isTablet ? 'tablet-landscape' : 'desktop'}
                sx={{
                    flex: '1 0 320px',
                    minHeight: 320,
                    display: 'grid',
                    gridTemplateColumns: compact ? '1fr' : 'minmax(180px, 0.2fr) minmax(0, 1fr)',
                    gridTemplateRows: compact ? 'auto minmax(0, 1fr)' : 'minmax(0, 1fr)',
                    gap: compact ? 0 : 2,
                    overflow: 'hidden',
                }}>
                <Box sx={{ minWidth: 0, overflow: compact ? 'hidden' : 'auto' }}>
                    <PanelSegment boundedHeight titleContent={<Typography>Proficiency Groups</Typography>}>
                        <MenuList
                            aria-label="Synthetic template proficiency groups"
                            sx={{ display: compact ? 'flex' : 'block', overflowX: compact ? 'auto' : 'visible', whiteSpace: compact ? 'nowrap' : 'normal' }}>
                            {graph.TemplateRevision2ProficiencyGroupDisplays.map(group => <MenuItem
                                key={group.Id}
                                selected={group.ProficiencyGroupId === selectedGroup?.ProficiencyGroupId}
                                onClick={() => setSelectedGroupId(group.ProficiencyGroupId)}
                                sx={{ minWidth: 0, minHeight: viewport.isDesktop ? undefined : 48 }}>
                                <Tooltip title={group.ProficiencyGroupName} enterDelay={0}>
                                    <Typography noWrap tabIndex={0} aria-label={`Full proficiency group name: ${group.ProficiencyGroupName}`}>
                                        {group.ProficiencyGroupName}
                                    </Typography>
                                </Tooltip>
                            </MenuItem>)}
                        </MenuList>
                    </PanelSegment>
                </Box>
                <Box sx={{ minWidth: 0, minHeight: 0, overflow: 'hidden' }}>
                    {selectedGroup && <PanelSegment boundedHeight titleContent={<Typography>{selectedGroup.ProficiencyGroupName}</Typography>}>
                        <Box sx={{ height: '100%', minHeight: 0, width: '100%' }}>
                            {!graph.Proficiencies.some(proficiency => proficiency.ProficiencyGroupId === selectedGroup.ProficiencyGroupId) && <Alert
                                severity="info"
                                data-testid="synthetic-template-empty-group"
                                sx={{ mb: 1 }}>
                                No registered criteria in this category.
                            </Alert>}
                            <ProficiencyGraphsGrid
                                profileGraph={graph}
                                selectedProficiencyGroup={selectedGroup}
                                mode={UpsertAction.View}
                                loadData={async () => denyMutation()}
                                handleSetWeight={() => Promise.reject(new Error('Synthetic template replay is read-only.'))}
                                handleSelectionChanged={denyMutation}
                                handleRemove={denyMutation}
                                abilityLevel={replay.AbilityLegend}
                                behaviorLevel={replay.BehaviorLegend}
                                weightSelections={[]}
                                showReadOnlyLegendButtons={false}
                            />
                        </Box>
                    </PanelSegment>}
                </Box>
            </Box>
        </DialogContent>
        <StickyDialogActions viewportOverride={viewport.mode}>
            {onBack && <ButtonEx onClick={onBack} variant="text" color="inherit">Back to role</ButtonEx>}
            <ButtonEx onClick={onClose} variant="text" color="inherit">Close</ButtonEx>
        </StickyDialogActions>
    </ResponsiveDialog>;
};

export default SyntheticTemplateReplayPresentation;
